<script setup lang="ts">
import {
    Check,
    ChevronLeft,
    Clock3,
    Lock,
    Repeat,
    Sparkles,
    Trophy,
    Zap,
} from 'lucide-vue-next';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { t } from '../i18n';
import {
    amount,
    conditionText,
    multiplierText,
    progressText,
    questWhen,
    rarity,
} from '../loyaltyText';
import type { ClubLoyalty, LoyaltyAchievement, LoyaltyQuest } from '../types';
import BrandAvatar from './BrandAvatar.vue';

/**
 * «Квести й досягнення» (Черняк, 2026-10-15): what the player can do at this club for its XP,
 * and how far along they are. XP and the level come only from quests and achievements, and a
 * reward nobody sees steers nobody — so the screen leads with the level, then today's quests,
 * then the club's achievements by how close each one is.
 *
 * Opened from the network screen; with several clubs the chips on top switch between them.
 */
const props = defineProps<{
    open: boolean;
    data: ClubLoyalty | null;
    loading: boolean;
    /** The network's clubs that show their quests, for the chips. */
    clubs: { id: number; name: string; logo_url: string | null }[];
    clubId: number | null;
}>();

defineEmits<{ close: []; selectClub: [number] }>();

const tab = ref<'quests' | 'achievements'>('quests');

/** Bars start empty and fill once drawn: the motion says «this is your progress». */
const filled = ref(false);

async function fill(): Promise<void> {
    filled.value = false;

    if (props.open && props.data) {
        await nextTick();
        requestAnimationFrame(() => (filled.value = true));
    }
}

watch(() => [props.open, props.data], fill);
onMounted(fill);

watch(
    () => props.clubId,
    () => (tab.value = 'quests'),
);

const loyalty = computed(() => props.data?.loyalty ?? null);
const board = computed(() => loyalty.value?.board ?? null);
const currency = computed(
    () => board.value?.currency ?? loyalty.value?.currency ?? 'XP',
);
const quests = computed(() => board.value?.quests ?? []);
const achievements = computed(() => board.value?.achievements ?? []);
const doneAchievements = computed(
    () => achievements.value.filter((a) => a.state === 'done').length,
);
const openQuests = computed(
    () => quests.value.filter((q) => q.state !== 'done').length,
);

const levelColor = computed(() => loyalty.value?.level.color || '#2997ff');

function width(pct: number): string {
    return `${filled.value ? Math.max(pct, pct > 0 ? 4 : 0) : 0}%`;
}

/** The condition still furthest from done — the one the bar is waiting on. */
function slowest(item: LoyaltyQuest | LoyaltyAchievement) {
    return [...item.criteria].sort(
        (a, b) => a.current / (a.qty || 1) - b.current / (b.qty || 1),
    )[0];
}

function ring(a: LoyaltyAchievement): Record<string, string> {
    const pct = filled.value ? a.progress_pct : 0;

    return {
        '--rarity': rarity(a.rarity).color,
        '--pct': `${pct}%`,
    };
}
</script>

<template>
    <Transition name="lq-page" appear>
        <section
            v-if="open"
            class="lq-page"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lq-title"
            :style="{ '--level': levelColor }"
        >
            <!-- A screen of its own, not a sheet: a short list left half a sheet with the
                 dashboard scrolling under it (2026-10-16). -->
            <nav class="lq-topbar">
                <button class="lq-back" type="button" @click="$emit('close')">
                    <ChevronLeft :size="20" />
                    <span>{{ t('Назад') }}</span>
                </button>
                <span class="lq-topbar__title">{{
                    t('Квести й досягнення')
                }}</span>
            </nav>

            <div class="lq-scroll">
                <!-- The level: the thing every quest below feeds. -->
                <header class="lq-hero">
                    <span class="lq-hero__aurora" aria-hidden="true" />

                    <div
                        v-if="clubs.length > 1"
                        class="lq-clubs"
                        role="tablist"
                    >
                        <button
                            v-for="club in clubs"
                            :key="club.id"
                            class="lq-club-chip"
                            :class="{ 'is-active': club.id === clubId }"
                            type="button"
                            role="tab"
                            :aria-selected="club.id === clubId"
                            @click="$emit('selectClub', club.id)"
                        >
                            <BrandAvatar
                                :src="club.logo_url"
                                :name="club.name"
                                size="sm"
                            />
                            <span>{{ club.name }}</span>
                        </button>
                    </div>
                    <span v-else-if="data" class="eyebrow lq-club-name">{{
                        data.club.name
                    }}</span>

                    <template v-if="loyalty">
                        <div class="lq-level">
                            <span class="lq-level__badge" aria-hidden="true">
                                <img
                                    v-if="loyalty.level.image_url"
                                    :src="loyalty.level.image_url"
                                    alt=""
                                />
                                <template v-else-if="loyalty.level.icon">{{
                                    loyalty.level.icon
                                }}</template>
                                <Trophy v-else :size="26" :stroke-width="1.8" />
                            </span>
                            <span class="lq-level__copy">
                                <span class="lq-level__label">{{
                                    t('Ваш рівень')
                                }}</span>
                                <h2 id="lq-title">{{ loyalty.level.name }}</h2>
                            </span>
                            <span class="lq-xp-chip">
                                <Sparkles :size="14" :stroke-width="2.2" />
                                {{ amount(loyalty.balance_xp) }}
                                {{ currency }}
                            </span>
                        </div>

                        <div class="lq-level-bar">
                            <span
                                class="lq-level-bar__fill"
                                :style="{ width: width(loyalty.progress_pct) }"
                            />
                        </div>
                        <p class="lq-level-next">
                            <template v-if="loyalty.next_level">
                                {{
                                    t('Ще {xp} {currency} до рівня {level}', {
                                        xp: amount(
                                            loyalty.next_level.xp_remaining,
                                        ),
                                        currency,
                                        level: loyalty.next_level.name,
                                    })
                                }}
                            </template>
                            <template v-else>{{
                                t('Найвищий рівень клубу')
                            }}</template>
                        </p>
                    </template>
                    <h2 v-else id="lq-title" class="lq-title-plain">
                        {{ t('Квести й досягнення') }}
                    </h2>
                </header>

                <!-- Skeleton while the club's board loads. -->
                <div
                    v-if="loading && !data"
                    class="lq-skeleton"
                    aria-hidden="true"
                >
                    <span class="lq-skeleton__hero" />
                    <span /><span /><span />
                </div>

                <template v-else-if="board">
                    <!-- An event multiplier in force right now. -->
                    <div
                        v-for="m in board.multipliers"
                        :key="m.id"
                        class="lq-boost"
                    >
                        <span class="lq-boost__icon" aria-hidden="true">{{
                            m.icon || '🔥'
                        }}</span>
                        <span class="lq-boost__copy">
                            <strong>{{ multiplierText(m, currency) }}</strong>
                            <span>
                                {{ m.name
                                }}<template v-if="m.zone">
                                    ·
                                    {{
                                        t('у зоні {zone}', { zone: m.zone })
                                    }}</template
                                ><template v-if="m.until">
                                    ·
                                    {{
                                        t('до {time}', { time: m.until })
                                    }}</template
                                >
                            </span>
                        </span>
                        <Zap
                            class="lq-boost__zap"
                            :size="18"
                            aria-hidden="true"
                        />
                    </div>

                    <div class="lq-tabs" role="tablist">
                        <span
                            class="lq-tabs__pill"
                            :class="{ 'is-right': tab === 'achievements' }"
                            aria-hidden="true"
                        />
                        <button
                            type="button"
                            role="tab"
                            :aria-selected="tab === 'quests'"
                            :class="{ 'is-active': tab === 'quests' }"
                            @click="tab = 'quests'"
                        >
                            {{ t('Квести') }}
                            <span v-if="openQuests" class="lq-tabs__count">{{
                                openQuests
                            }}</span>
                        </button>
                        <button
                            type="button"
                            role="tab"
                            :aria-selected="tab === 'achievements'"
                            :class="{ 'is-active': tab === 'achievements' }"
                            @click="tab = 'achievements'"
                        >
                            {{ t('Досягнення') }}
                            <span class="lq-tabs__count lq-tabs__count--soft"
                                >{{ doneAchievements }}/{{
                                    achievements.length
                                }}</span
                            >
                        </button>
                    </div>

                    <Transition name="lq-swap" mode="out-in">
                        <!-- Today's quests. -->
                        <div
                            v-if="tab === 'quests'"
                            key="quests"
                            class="lq-list"
                        >
                            <p v-if="!quests.length" class="lq-empty">
                                <span class="lq-empty__art" aria-hidden="true"
                                    >🗺️</span
                                >
                                <strong>{{
                                    t('Сьогодні квестів немає')
                                }}</strong>
                                <span>{{
                                    t(
                                        'Загляньте пізніше: клуб додає нові завдання, і за них дають {currency}.',
                                        { currency },
                                    )
                                }}</span>
                            </p>

                            <article
                                v-for="(q, i) in quests"
                                :key="q.id"
                                class="lq-quest"
                                :class="`is-${q.state}`"
                                :style="{ '--delay': `${i * 60}ms` }"
                            >
                                <span class="lq-quest__icon" aria-hidden="true">
                                    {{ q.icon || '⚡' }}
                                    <span
                                        v-if="q.state === 'done'"
                                        class="lq-quest__done"
                                    >
                                        <Check :size="13" :stroke-width="3" />
                                    </span>
                                </span>
                                <span class="lq-quest__body">
                                    <span class="lq-quest__top">
                                        <span class="lq-quest__name">{{
                                            q.name
                                        }}</span>
                                        <span class="lq-reward"
                                            >+{{ amount(q.xp) }}
                                            {{ currency }}</span
                                        >
                                    </span>
                                    <span
                                        v-for="(c, ci) in q.criteria"
                                        :key="ci"
                                        class="lq-quest__condition"
                                        >{{ conditionText(c) }}</span
                                    >
                                    <span
                                        v-if="q.description"
                                        class="lq-quest__desc"
                                        >{{ q.description }}</span
                                    >
                                    <span
                                        class="lq-bar"
                                        :class="`is-${q.state}`"
                                    >
                                        <span
                                            class="lq-bar__fill"
                                            :style="{
                                                width: width(q.progress_pct),
                                            }"
                                        />
                                    </span>
                                    <span class="lq-quest__meta">
                                        <span
                                            v-if="q.state === 'done'"
                                            class="lq-state lq-state--done"
                                        >
                                            <Check
                                                :size="13"
                                                :stroke-width="2.6"
                                            />
                                            {{ t('Виконано') }}
                                        </span>
                                        <span
                                            v-else-if="q.state === 'upcoming'"
                                            class="lq-state lq-state--soon"
                                        >
                                            <Lock
                                                :size="12"
                                                :stroke-width="2.4"
                                            />
                                            {{ t('Почнеться') }}
                                            {{ questWhen(q) }}
                                        </span>
                                        <span v-else class="lq-progress-text">{{
                                            slowest(q)
                                                ? progressText(slowest(q)!)
                                                : `${q.progress_pct}%`
                                        }}</span>
                                        <span
                                            v-if="q.state !== 'upcoming'"
                                            class="lq-when"
                                        >
                                            <Clock3
                                                :size="12"
                                                :stroke-width="2.2"
                                            />
                                            {{ questWhen(q) }}
                                        </span>
                                    </span>
                                </span>
                            </article>
                        </div>

                        <!-- The club's achievements, nearest to done first. -->
                        <div v-else key="achievements" class="lq-grid">
                            <p
                                v-if="!achievements.length"
                                class="lq-empty lq-empty--wide"
                            >
                                <span class="lq-empty__art" aria-hidden="true"
                                    >🏆</span
                                >
                                <strong>{{
                                    t('У клубі поки немає досягнень')
                                }}</strong>
                            </p>

                            <article
                                v-for="(a, i) in achievements"
                                :key="a.id"
                                class="lq-ach"
                                :class="[`is-${a.state}`, `rarity-${a.rarity}`]"
                                :style="{
                                    ...ring(a),
                                    '--delay': `${i * 45}ms`,
                                }"
                            >
                                <span class="lq-ach__ring" aria-hidden="true">
                                    <span class="lq-ach__emoji">{{
                                        a.icon || '🏅'
                                    }}</span>
                                    <span
                                        v-if="a.state === 'done'"
                                        class="lq-ach__check"
                                    >
                                        <Check :size="12" :stroke-width="3" />
                                    </span>
                                </span>
                                <span class="lq-ach__rarity">{{
                                    rarity(a.rarity).label
                                }}</span>
                                <span class="lq-ach__name">{{ a.name }}</span>
                                <span class="lq-ach__condition">{{
                                    a.criteria[0]
                                        ? conditionText(a.criteria[0])
                                        : a.description
                                }}</span>
                                <span class="lq-ach__foot">
                                    <span class="lq-reward lq-reward--small"
                                        >+{{ amount(a.xp) }}
                                        {{ currency }}</span
                                    >
                                    <span
                                        v-if="a.state === 'done'"
                                        class="lq-ach__got"
                                        >{{ t('Отримано') }}</span
                                    >
                                    <span v-else class="lq-ach__pct"
                                        >{{ a.progress_pct }}%</span
                                    >
                                </span>
                                <span
                                    v-if="a.is_repeatable"
                                    class="lq-ach__repeat"
                                    :aria-label="
                                        t('Виконано {n} разів', {
                                            n: a.times_done,
                                        })
                                    "
                                >
                                    <Repeat :size="11" :stroke-width="2.6" />
                                    ×{{ a.times_done }}
                                </span>
                            </article>
                        </div>
                    </Transition>
                </template>

                <p v-else-if="data" class="lq-empty lq-empty--hidden">
                    <span class="lq-empty__art" aria-hidden="true">🌙</span>
                    <strong>{{ t('Клуб поки не показує квести') }}</strong>
                </p>
            </div>
        </section>
    </Transition>
</template>

<style scoped>
.lq-page {
    position: fixed;
    z-index: 45;
    inset: 0;
    display: flex;
    flex-direction: column;
    background: var(--bg);
    text-align: left;
}
/* Below Telegram's own «Закрити» and menu in fullscreen. */
.lq-topbar {
    position: relative;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    height: calc(48px + var(--app-safe-area-top));
    padding: var(--app-safe-area-top) 12px 0;
    box-sizing: border-box;
}
.lq-back {
    position: absolute;
    bottom: 7px;
    left: 8px;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    height: 34px;
    padding: 0 10px 0 4px;
    border: 0;
    border-radius: 10px;
    background: none;
    color: var(--accent);
    font: 600 15px var(--app-font);
    cursor: pointer;
}
.lq-topbar__title {
    max-width: calc(100% - 200px);
    overflow: hidden;
    font: 700 15px var(--app-font);
    text-overflow: ellipsis;
    white-space: nowrap;
}
.lq-scroll {
    flex: 1 1 auto;
    min-height: 0;
    padding-bottom: calc(18px + env(safe-area-inset-bottom));
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}
.lq-page-enter-active,
.lq-page-leave-active {
    transition:
        transform 0.32s cubic-bezier(0.22, 0.88, 0.28, 1),
        opacity 0.24s ease;
}
.lq-page-enter-from,
.lq-page-leave-to {
    transform: translate3d(100%, 0, 0);
    opacity: 0.6;
}

/* ===== The level ===== */
.lq-hero {
    position: relative;
    overflow: hidden;
    margin: 4px 12px 0;
    padding: 20px 18px 18px;
    border-radius: 24px;
    background:
        radial-gradient(
            120% 90% at 0% 0%,
            color-mix(in srgb, var(--level) 34%, transparent),
            transparent 60%
        ),
        radial-gradient(
            110% 80% at 100% 10%,
            color-mix(in srgb, var(--accent) 26%, transparent),
            transparent 62%
        ),
        var(--surface);
}
.lq-hero__aurora {
    position: absolute;
    inset: -40% -20% auto;
    height: 220px;
    background: conic-gradient(
        from 90deg,
        transparent,
        color-mix(in srgb, var(--level) 22%, transparent),
        transparent,
        color-mix(in srgb, var(--accent) 18%, transparent),
        transparent
    );
    filter: blur(28px);
    opacity: 0.8;
    animation: lq-aurora 14s linear infinite;
    pointer-events: none;
}
.lq-hero > *:not(.lq-hero__aurora) {
    position: relative;
}
.lq-club-name {
    display: block;
    margin: 0 0 14px;
}
.lq-clubs {
    display: flex;
    gap: 8px;
    margin: 0 0 16px;
    overflow-x: auto;
    scrollbar-width: none;
}
.lq-clubs::-webkit-scrollbar {
    display: none;
}
.lq-club-chip {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 7px;
    max-width: 180px;
    padding: 5px 12px 5px 5px;
    border: 1px solid color-mix(in srgb, var(--hint) 22%, transparent);
    border-radius: 999px;
    background: color-mix(in srgb, var(--surface) 70%, transparent);
    color: var(--text);
    font: 600 12.5px var(--app-font);
    cursor: pointer;
    backdrop-filter: blur(8px);
}
.lq-club-chip span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.lq-club-chip.is-active {
    border-color: color-mix(in srgb, var(--accent) 60%, transparent);
    background: color-mix(in srgb, var(--accent) 16%, var(--surface));
}
.lq-level {
    display: flex;
    align-items: center;
    gap: 14px;
}
.lq-level__badge {
    display: grid;
    width: 58px;
    height: 58px;
    flex: 0 0 58px;
    place-items: center;
    overflow: hidden;
    border-radius: 18px;
    background: linear-gradient(
        145deg,
        color-mix(in srgb, var(--level) 92%, #fff),
        color-mix(in srgb, var(--level) 70%, #000)
    );
    box-shadow:
        0 10px 28px color-mix(in srgb, var(--level) 45%, transparent),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
    color: #fff;
    font-size: 28px;
}
.lq-level__badge img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.lq-level__copy {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 2px;
}
.lq-level__label {
    color: var(--hint);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}
.lq-level h2,
.lq-title-plain {
    margin: 0;
    overflow: hidden;
    color: var(--text);
    font-size: 24px;
    font-weight: 750;
    letter-spacing: -0.02em;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.lq-xp-chip {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 5px;
    padding: 7px 11px;
    border-radius: 999px;
    background: linear-gradient(135deg, #ffd66b, #ff9f1c);
    box-shadow: 0 6px 16px rgba(255, 159, 28, 0.32);
    color: #3b2400;
    font-size: 13px;
    font-weight: 750;
    font-variant-numeric: tabular-nums;
}
.lq-level-bar {
    position: relative;
    height: 10px;
    margin-top: 18px;
    overflow: hidden;
    border-radius: 999px;
    background: color-mix(in srgb, var(--hint) 18%, transparent);
}
.lq-level-bar__fill {
    position: absolute;
    inset: 0 auto 0 0;
    border-radius: inherit;
    background: linear-gradient(90deg, var(--level), var(--accent));
    box-shadow: 0 0 14px color-mix(in srgb, var(--accent) 55%, transparent);
    transition: width 1.1s cubic-bezier(0.22, 1, 0.36, 1);
}
.lq-level-bar__fill::after,
.lq-bar__fill::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
        100deg,
        transparent 20%,
        rgba(255, 255, 255, 0.45) 50%,
        transparent 80%
    );
    transform: translateX(-100%);
    animation: lq-shine 2.8s ease-in-out 1.2s infinite;
}
.lq-level-next {
    margin: 9px 0 0;
    color: var(--hint);
    font-size: 12.5px;
}

/* ===== Multiplier in force ===== */
.lq-boost {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 14px 16px 0;
    padding: 12px 14px;
    overflow: hidden;
    border-radius: 16px;
    background: linear-gradient(
        110deg,
        #ff6b3d,
        #ff3d7f 45%,
        #b14dff 80%,
        #ff6b3d
    );
    background-size: 220% 100%;
    box-shadow: 0 10px 24px rgba(255, 61, 127, 0.3);
    color: #fff;
    animation: lq-boost 6s ease-in-out infinite;
}
.lq-boost__icon {
    font-size: 24px;
}
.lq-boost__copy {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    font-size: 12.5px;
}
.lq-boost__copy strong {
    font-size: 16px;
    font-weight: 800;
}
.lq-boost__zap {
    opacity: 0.85;
}

/* ===== Tabs ===== */
.lq-tabs {
    position: sticky;
    top: 0;
    z-index: 2;
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 16px 16px 4px;
    padding: 4px;
    border-radius: 14px;
    background: var(--secondary);
}
.lq-tabs__pill {
    position: absolute;
    top: 4px;
    bottom: 4px;
    left: 4px;
    width: calc(50% - 4px);
    border-radius: 11px;
    background: var(--surface);
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.12);
    transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
.lq-tabs__pill.is-right {
    transform: translateX(100%);
}
.lq-tabs button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 38px;
    border: 0;
    background: none;
    color: var(--hint);
    font: 650 14px var(--app-font);
    cursor: pointer;
    transition: color 0.2s;
}
.lq-tabs button.is-active {
    color: var(--text);
}
.lq-tabs__count {
    display: inline-grid;
    min-width: 20px;
    height: 20px;
    place-items: center;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--accent);
    color: #fff;
    font-size: 11.5px;
    font-variant-numeric: tabular-nums;
}
.lq-tabs__count--soft {
    background: color-mix(in srgb, var(--hint) 18%, transparent);
    color: var(--hint);
}

/* ===== Quests ===== */
.lq-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 16px 6px;
}
.lq-quest {
    display: flex;
    gap: 13px;
    padding: 14px;
    border: 1px solid color-mix(in srgb, var(--hint) 14%, transparent);
    border-radius: 18px;
    background: var(--surface);
    box-shadow: 0 4px 18px rgba(15, 23, 42, 0.05);
    animation: lq-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) var(--delay) both;
}
.lq-quest.is-active {
    border-color: color-mix(in srgb, var(--accent) 30%, transparent);
    background:
        linear-gradient(
            160deg,
            color-mix(in srgb, var(--accent) 9%, transparent),
            transparent 55%
        ),
        var(--surface);
}
.lq-quest.is-upcoming {
    opacity: 0.72;
}
.lq-quest.is-done {
    border-color: color-mix(in srgb, #22c55e 34%, transparent);
}
.lq-quest__icon {
    position: relative;
    display: grid;
    width: 50px;
    height: 50px;
    flex: 0 0 50px;
    place-items: center;
    border-radius: 15px;
    background: linear-gradient(
        150deg,
        color-mix(in srgb, var(--accent) 22%, var(--surface)),
        color-mix(in srgb, var(--level) 18%, var(--secondary))
    );
    font-size: 25px;
}
.lq-quest__done {
    position: absolute;
    right: -5px;
    bottom: -5px;
    display: grid;
    width: 22px;
    height: 22px;
    place-items: center;
    border: 2px solid var(--surface);
    border-radius: 50%;
    background: #22c55e;
    color: #fff;
    animation: lq-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
.lq-quest__body {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 3px;
}
.lq-quest__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}
.lq-quest__name {
    color: var(--text);
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
}
.lq-quest__condition {
    color: var(--text);
    font-size: 13px;
    line-height: 1.4;
    opacity: 0.85;
}
.lq-quest__desc {
    color: var(--hint);
    font-size: 12px;
    line-height: 1.4;
}
.lq-reward {
    flex: 0 0 auto;
    padding: 4px 9px;
    border-radius: 999px;
    background: linear-gradient(135deg, #ffd66b, #ffab2e);
    color: #3b2400;
    font-size: 12px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}
.lq-reward--small {
    padding: 3px 8px;
    font-size: 11px;
}
.lq-bar {
    position: relative;
    height: 7px;
    margin-top: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: color-mix(in srgb, var(--hint) 16%, transparent);
}
.lq-bar__fill {
    position: absolute;
    inset: 0 auto 0 0;
    border-radius: inherit;
    background: linear-gradient(90deg, #4fb4ff, var(--accent) 55%, #7a5cff);
    transition: width 1s cubic-bezier(0.22, 1, 0.36, 1) var(--delay);
}
.lq-bar.is-done .lq-bar__fill {
    background: linear-gradient(90deg, #34d399, #22c55e);
}
.lq-bar.is-upcoming .lq-bar__fill {
    background: color-mix(in srgb, var(--hint) 40%, transparent);
}
.lq-quest__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 6px;
    font-size: 12px;
}
.lq-progress-text {
    color: var(--text);
    font-weight: 650;
    font-variant-numeric: tabular-nums;
}
.lq-when,
.lq-state {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--hint);
    font-weight: 600;
}
.lq-state--done {
    color: #16a34a;
}
.lq-state--soon {
    color: var(--accent);
}

/* ===== Achievements ===== */
.lq-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding: 10px 16px 6px;
}
.lq-ach {
    position: relative;
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 16px 12px 12px;
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--rarity) 26%, transparent);
    border-radius: 20px;
    background:
        radial-gradient(
            120% 70% at 50% 0%,
            color-mix(in srgb, var(--rarity) 14%, transparent),
            transparent 70%
        ),
        var(--surface);
    text-align: center;
    animation: lq-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) var(--delay) both;
}
.lq-ach.is-done {
    border-color: color-mix(in srgb, var(--rarity) 60%, transparent);
    box-shadow: 0 8px 24px color-mix(in srgb, var(--rarity) 24%, transparent);
}
.lq-ach.rarity-legendary.is-done::before {
    content: '';
    position: absolute;
    inset: -50%;
    background: conic-gradient(
        from 0deg,
        transparent 0 70%,
        color-mix(in srgb, var(--rarity) 55%, transparent) 80%,
        transparent 90%
    );
    animation: lq-spin 4s linear infinite;
    pointer-events: none;
}
.lq-ach.rarity-legendary.is-done::after {
    content: '';
    position: absolute;
    inset: 1.5px;
    border-radius: 19px;
    background:
        radial-gradient(
            120% 70% at 50% 0%,
            color-mix(in srgb, var(--rarity) 16%, transparent),
            transparent 70%
        ),
        var(--surface);
    pointer-events: none;
}
.lq-ach > * {
    position: relative;
    z-index: 1;
}
.lq-ach__ring {
    position: relative;
    display: grid;
    width: 66px;
    height: 66px;
    place-items: center;
    margin-bottom: 4px;
    border-radius: 50%;
    background: conic-gradient(
        var(--rarity) var(--pct),
        color-mix(in srgb, var(--hint) 16%, transparent) 0
    );
    transition: --pct 1s;
}
.lq-ach__ring::before {
    content: '';
    position: absolute;
    inset: 5px;
    border-radius: 50%;
    background: var(--surface);
}
.lq-ach__emoji {
    position: relative;
    font-size: 27px;
}
.lq-ach.is-progress .lq-ach__emoji {
    filter: saturate(0.75);
}
.lq-ach__check {
    position: absolute;
    right: -2px;
    bottom: -2px;
    display: grid;
    width: 22px;
    height: 22px;
    place-items: center;
    border: 2px solid var(--surface);
    border-radius: 50%;
    background: var(--rarity);
    color: #fff;
}
.lq-ach__rarity {
    color: var(--rarity);
    font-size: 10.5px;
    font-weight: 750;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}
.lq-ach__name {
    width: 100%;
    overflow: hidden;
    color: var(--text);
    font-size: 14px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.lq-ach__condition {
    display: -webkit-box;
    min-height: 2.7em;
    overflow: hidden;
    color: var(--hint);
    font-size: 11.5px;
    line-height: 1.35;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}
.lq-ach__foot {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    margin-top: 6px;
}
.lq-ach__pct {
    color: var(--text);
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}
.lq-ach__got {
    color: var(--rarity);
    font-size: 11.5px;
    font-weight: 750;
}
.lq-ach__repeat {
    position: absolute;
    top: 10px;
    right: 10px;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 6px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--rarity) 18%, var(--secondary));
    color: var(--rarity);
    font-size: 10.5px;
    font-weight: 800;
}

/* ===== Empty and loading ===== */
.lq-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin: 18px 0 8px;
    padding: 0 24px;
    color: var(--hint);
    font-size: 13px;
    line-height: 1.5;
    text-align: center;
}
.lq-empty--wide {
    grid-column: 1 / -1;
}
.lq-empty--hidden {
    margin: 26px 0 18px;
}
.lq-empty strong {
    color: var(--text);
    font-size: 15px;
}
.lq-empty__art {
    font-size: 44px;
    animation: lq-float 3.6s ease-in-out infinite;
}
.lq-skeleton {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
}
.lq-skeleton span {
    height: 86px;
    border-radius: 18px;
    background: linear-gradient(
        100deg,
        var(--secondary) 30%,
        color-mix(in srgb, var(--surface) 70%, var(--secondary)) 50%,
        var(--secondary) 70%
    );
    background-size: 220% 100%;
    animation: lq-skeleton 1.3s ease-in-out infinite;
}
.lq-skeleton__hero {
    height: 44px !important;
}

/* ===== Motion ===== */
.lq-swap-enter-active,
.lq-swap-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.25s ease;
}
.lq-swap-enter-from {
    opacity: 0;
    transform: translateY(8px);
}
.lq-swap-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
@keyframes lq-rise {
    from {
        opacity: 0;
        transform: translateY(12px) scale(0.98);
    }
    to {
        opacity: 1;
        transform: none;
    }
}
@keyframes lq-pop {
    from {
        transform: scale(0);
    }
    to {
        transform: scale(1);
    }
}
@keyframes lq-shine {
    0% {
        transform: translateX(-100%);
    }
    60%,
    100% {
        transform: translateX(100%);
    }
}
@keyframes lq-aurora {
    to {
        transform: rotate(360deg);
    }
}
@keyframes lq-spin {
    to {
        transform: rotate(360deg);
    }
}
@keyframes lq-boost {
    0%,
    100% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
}
@keyframes lq-float {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-5px);
    }
}
@keyframes lq-skeleton {
    from {
        background-position: 100% 0;
    }
    to {
        background-position: -100% 0;
    }
}
@media (prefers-reduced-motion: reduce) {
    .lq-hero__aurora,
    .lq-boost,
    .lq-empty__art,
    .lq-ach.rarity-legendary.is-done::before,
    .lq-level-bar__fill::after,
    .lq-bar__fill::after,
    .lq-skeleton span {
        animation: none;
    }
    .lq-quest,
    .lq-ach,
    .lq-quest__done {
        animation: none;
    }
    .lq-level-bar__fill,
    .lq-bar__fill,
    .lq-tabs__pill {
        transition: none;
    }
}
</style>
